import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  HelpCircle,
  Home,
  Calendar,
  FileText,
  Users,
  DollarSign,
  FolderKanban,
  CheckSquare,
  AlertTriangle,
  Utensils,
  Clock,
  Package,
  ChevronRight,
  ChevronLeft,
  Lightbulb,
  BookOpen,
  Play,
  Heart,
  MessageSquarePlus,
  X,
  Target,
  Briefcase,
  Car,
  ClipboardList,
  LayoutDashboard,
} from 'lucide-react';

// Tour guiado interativo
const GuidedTour = ({ isOpen, onClose, isAdmin }) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Steps diferentes para admin vs usuário comum
  const tourSteps = isAdmin ? [
    {
      title: "Bem-vindo! 👋",
      content: "Este sistema é uma iniciativa para tornar o seu dia a dia mais organizado. Vamos fazer um tour rápido pelas principais funcionalidades?",
      icon: Heart,
      color: "text-rose-500"
    },
    {
      title: "Dashboard",
      content: "Sua central de controle! Veja o calendário de OSs, tarefas pendentes e acompanhe o Ciclo de Melhoria da empresa.",
      icon: Home,
      color: "text-blue-500"
    },
    {
      title: "Escala do Dia",
      content: "Registre a presença da equipe, apontamentos de serviço, refeições e controle de veículos. Tudo em um só lugar!",
      icon: Calendar,
      color: "text-green-500"
    },
    {
      title: "Humanas (RH)",
      content: "Cadastre colaboradores e terceiros, registre ocorrências e acompanhe o histórico de cada pessoa.",
      icon: Users,
      color: "text-amber-500"
    },
    {
      title: "Projetos e Tarefas",
      content: "Organize projetos por departamento, crie tarefas com prazos e acompanhe o progresso da equipe.",
      icon: FolderKanban,
      color: "text-violet-500"
    },
    {
      title: "Ciclo de Melhoria",
      content: "Acompanhe as metas mensais, indicadores de performance e contribua com ideias no Banco de Ideias.",
      icon: Target,
      color: "text-cyan-500"
    },
    {
      title: "Sua participação é importante! 💡",
      content: "Use o botão 'Sugerir' no menu lateral para enviar suas ideias e melhorias. Sua colaboração torna o sistema cada vez melhor para todos!",
      icon: MessageSquarePlus,
      color: "text-violet-500"
    }
  ] : [
    {
      title: "Bem-vindo! 👋",
      content: "Este sistema é uma iniciativa para tornar o seu dia a dia mais organizado. Vamos fazer um tour rápido pelas principais funcionalidades?",
      icon: Heart,
      color: "text-rose-500"
    },
    {
      title: "Dashboard",
      content: "Sua página inicial! Veja as OSs da semana, suas tarefas e as novidades do sistema.",
      icon: Home,
      color: "text-blue-500"
    },
    {
      title: "Escala do Dia",
      content: "Registre sua presença, apontamentos de serviço e veja as refeições do dia.",
      icon: Calendar,
      color: "text-green-500"
    },
    {
      title: "Projetos e Tarefas",
      content: "Veja os projetos em andamento e as tarefas atribuídas a você.",
      icon: FolderKanban,
      color: "text-violet-500"
    },
    {
      title: "Banco de Ideias",
      content: "Compartilhe suas ideias de melhoria! Cada sugestão ajuda a empresa a evoluir.",
      icon: Lightbulb,
      color: "text-amber-500"
    },
    {
      title: "Sua participação é importante! 💡",
      content: "Use o botão 'Sugerir' no menu lateral para enviar feedback. Sua colaboração faz a diferença!",
      icon: MessageSquarePlus,
      color: "text-violet-500"
    }
  ];

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
      setCurrentStep(0);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onClose();
    setCurrentStep(0);
  };

  const step = tourSteps[currentStep];
  const Icon = step.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md dark:bg-slate-800 dark:border-slate-700">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700">
                <Icon className={`w-6 h-6 ${step.color}`} />
              </div>
              <DialogTitle className="dark:text-slate-100">{step.title}</DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-base pt-4 dark:text-slate-300">
            {step.content}
          </DialogDescription>
        </DialogHeader>
        
        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 py-4">
          {tourSteps.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentStep 
                  ? 'bg-violet-500' 
                  : index < currentStep 
                    ? 'bg-violet-300' 
                    : 'bg-slate-200 dark:bg-slate-600'
              }`}
            />
          ))}
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <div>
            {currentStep > 0 && (
              <Button variant="ghost" onClick={handlePrev} className="dark:text-slate-300">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleSkip} className="text-slate-500">
              Pular tour
            </Button>
            <Button onClick={handleNext} className="bg-violet-600 hover:bg-violet-700">
              {currentStep === tourSteps.length - 1 ? 'Concluir' : 'Próximo'}
              {currentStep < tourSteps.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const HelpPage = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('inicio');
  const [showTour, setShowTour] = useState(false);

  // Verificar se é admin/diretoria
  const isAdmin = ['admin', 'super_admin', 'diretoria'].includes(user?.role);

  // Verificar se é primeira visita
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('excelencia_tour_completed');
    if (!hasSeenTour) {
      setShowTour(true);
    }
  }, []);

  const handleCloseTour = () => {
    setShowTour(false);
    localStorage.setItem('excelencia_tour_completed', 'true');
  };

  // Seções disponíveis baseadas no perfil
  const allSections = [
    { id: 'inicio', label: 'Início Rápido', icon: Home, forAll: true },
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, forAll: true },
    { id: 'escala', label: 'Escala do Dia', icon: Calendar, forAll: true },
    { id: 'humanas', label: 'Humanas (RH)', icon: Users, adminOnly: true },
    { id: 'projetos', label: 'Projetos e Tarefas', icon: FolderKanban, forAll: true },
    { id: 'ciclo', label: 'Ciclo de Melhoria', icon: Target, forAll: true },
    { id: 'dicas', label: 'Dicas', icon: Lightbulb, forAll: true },
  ];

  const sections = allSections.filter(s => s.forAll || (s.adminOnly && isAdmin));

  return (
    <div className="space-y-6">
      {/* Tour guiado */}
      <GuidedTour isOpen={showTour} onClose={handleCloseTour} isAdmin={isAdmin} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-violet-100 dark:bg-violet-900/30 rounded-xl">
            <BookOpen className="w-8 h-8 text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              Central de Ajuda
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Aprenda a usar as funcionalidades do sistema
            </p>
          </div>
        </div>
        <Button 
          onClick={() => setShowTour(true)} 
          variant="outline"
          className="gap-2 dark:border-slate-700"
        >
          <Play className="w-4 h-4" />
          Ver Tour Guiado
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Navegação lateral */}
        <Card className="lg:col-span-1 dark:bg-slate-800/50 dark:border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium dark:text-slate-300">Seções</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <nav className="space-y-1">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeSection === section.id
                        ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {section.label}
                    {activeSection === section.id && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </button>
                );
              })}
            </nav>
          </CardContent>
        </Card>

        {/* Conteúdo principal */}
        <Card className="lg:col-span-3 dark:bg-slate-800/50 dark:border-slate-700">
          <ScrollArea className="h-[600px]">
            <CardContent className="p-6">
              {/* INÍCIO RÁPIDO */}
              {activeSection === 'inicio' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold mb-4 dark:text-slate-100">Bem-vindo!</h2>
                    <div className="p-4 bg-gradient-to-r from-violet-50 to-rose-50 dark:from-violet-900/20 dark:to-rose-900/20 rounded-lg border border-violet-100 dark:border-violet-800 mb-4">
                      <p className="text-slate-700 dark:text-slate-300">
                        Este sistema é uma <strong>iniciativa para tornar o dia a dia mais organizado</strong>. 
                        Sua colaboração por meio das sugestões no próprio sistema tornará cada dia mais produtivo para todos!
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 flex items-center gap-2">
                        <MessageSquarePlus className="w-4 h-4" />
                        Use o botão "Sugerir" no menu para enviar suas ideias
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
                      <h3 className="font-medium text-cyan-800 dark:text-cyan-400 mb-2 flex items-center gap-2">
                        <LayoutDashboard className="w-4 h-4" />
                        Dashboard
                      </h3>
                      <p className="text-sm text-cyan-700 dark:text-cyan-300">
                        Veja o calendário de OSs, tarefas da semana e acompanhe as metas.
                      </p>
                    </div>

                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <h3 className="font-medium text-green-800 dark:text-green-400 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Escala do Dia
                      </h3>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        Registre presenças, apontamentos, refeições e veículos.
                      </p>
                    </div>

                    <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                      <h3 className="font-medium text-violet-800 dark:text-violet-400 mb-2 flex items-center gap-2">
                        <FolderKanban className="w-4 h-4" />
                        Projetos
                      </h3>
                      <p className="text-sm text-violet-700 dark:text-violet-300">
                        Organize tarefas e acompanhe projetos da equipe.
                      </p>
                    </div>

                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <h3 className="font-medium text-amber-800 dark:text-amber-400 mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" />
                        Banco de Ideias
                      </h3>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        Compartilhe sugestões e veja ideias da equipe.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-100 dark:bg-slate-700/50 rounded-lg">
                    <h3 className="font-medium mb-2 dark:text-slate-200">Seu dia a dia no sistema:</h3>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600 dark:text-slate-400">
                      <li>Acesse o <strong>Dashboard</strong> para ver a visão geral</li>
                      <li>Na <strong>Escala do Dia</strong>, registre seus apontamentos</li>
                      <li>Verifique suas <strong>tarefas</strong> pendentes</li>
                      <li>Contribua com ideias no <strong>Banco de Ideias</strong></li>
                    </ol>
                  </div>
                </div>
              )}

              {/* DASHBOARD */}
              {activeSection === 'dashboard' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold dark:text-slate-100">Dashboard</h2>
                  
                  <p className="text-slate-600 dark:text-slate-400">
                    O Dashboard é sua central de controle. Aqui você encontra uma visão completa das atividades.
                  </p>

                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <h3 className="font-medium mb-2 dark:text-slate-200 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-cyan-500" />
                        Calendário Mensal
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400">
                        <li>Navegue entre os meses usando as setas</li>
                        <li>Clique em um dia para ver detalhes das OSs</li>
                        <li><span className="inline-block w-2 h-2 bg-amber-400 rounded-full"></span> Indicador de Montagem</li>
                        <li><span className="inline-block w-2 h-2 bg-emerald-400 rounded-full"></span> Indicador de Evento</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <h3 className="font-medium mb-2 dark:text-slate-200 flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-cyan-500" />
                        Visão Semanal (Gantt)
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400">
                        <li>Visualize todas as OSs e Tarefas da semana</li>
                        <li><span className="text-amber-500 font-medium">Amarelo</span> = Período de Montagem</li>
                        <li><span className="text-emerald-500 font-medium">Verde</span> = Período do Evento</li>
                        <li><span className="text-violet-500 font-medium">Roxo</span> = Prazo de Tarefa</li>
                        <li>Use as setas para navegar entre semanas</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <h3 className="font-medium mb-2 dark:text-slate-200 flex items-center gap-2">
                        <Target className="w-4 h-4 text-cyan-500" />
                        Widgets
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400">
                        <li><strong>Ciclo de Melhoria:</strong> Acompanhe as metas mensais</li>
                        <li><strong>Banco de Ideias:</strong> Veja e contribua com sugestões</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* ESCALA DO DIA */}
              {activeSection === 'escala' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold dark:text-slate-100">Escala do Dia</h2>
                  
                  <p className="text-slate-600 dark:text-slate-400">
                    Registre a presença e atividades diárias da equipe.
                  </p>
                  
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="apontamentos">
                      <AccordionTrigger className="dark:text-slate-200">
                        <span className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-violet-500" />
                          Aba Apontamentos
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="dark:text-slate-400">
                        <div className="space-y-3 pl-6">
                          <p>Registre a presença e atividade de cada pessoa:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            <li><strong>Entrada/Saída:</strong> Horários do dia</li>
                            <li><strong>Local:</strong> Interno, Externo, Faltou, Folga, Banco de Horas, Atestado</li>
                            <li><strong>Tipo Serviço:</strong> Montagem, Evento, Desmontagem, etc.</li>
                            <li><strong>OS:</strong> Número da Ordem de Serviço vinculada</li>
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="terceiros">
                      <AccordionTrigger className="dark:text-slate-200">
                        <span className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-indigo-500" />
                          Aba Terceiros
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="dark:text-slate-400">
                        <div className="space-y-3 pl-6">
                          <p>Registre prestadores de serviço do dia:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>Adicione terceiros que trabalharam no dia</li>
                            <li>Informe o valor do serviço</li>
                            <li>Vincule à OS correspondente</li>
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="veiculos">
                      <AccordionTrigger className="dark:text-slate-200">
                        <span className="flex items-center gap-2">
                          <Car className="w-4 h-4 text-blue-500" />
                          Aba Veículos
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="dark:text-slate-400">
                        <div className="space-y-3 pl-6">
                          <p>Controle de veículos utilizados:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>Registre quilometragem inicial e final</li>
                            <li>Informe abastecimentos realizados</li>
                            <li>Adicione observações relevantes</li>
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="refeicoes">
                      <AccordionTrigger className="dark:text-slate-200">
                        <span className="flex items-center gap-2">
                          <Utensils className="w-4 h-4 text-green-500" />
                          Aba Refeições
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="dark:text-slate-400">
                        <div className="space-y-3 pl-6">
                          <p>Controle das refeições do dia:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>Registre por fornecedor</li>
                            <li>Marque quem recebeu refeição usando os botões rápidos</li>
                            <li><strong>Escala:</strong> Marca quem está na escala do dia</li>
                            <li><strong>Todos:</strong> Marca todos os presentes</li>
                            <li><strong>Limpar:</strong> Remove todas as marcações</li>
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="desperdicios">
                      <AccordionTrigger className="dark:text-slate-200">
                        <span className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                          Aba Desperdícios
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="dark:text-slate-400">
                        <div className="space-y-3 pl-6">
                          <p>Registre perdas e desperdícios:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>Material danificado ou perdido</li>
                            <li>Desvios de processo</li>
                            <li>Outros custos não planejados</li>
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              )}

              {/* HUMANAS (apenas para admin) */}
              {activeSection === 'humanas' && isAdmin && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold dark:text-slate-100">Humanas (RH)</h2>

                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <h3 className="font-medium mb-2 dark:text-slate-200">Tipos de Pessoas</h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <Badge className="bg-blue-100 text-blue-800 mb-1">Colaborador</Badge>
                          <p className="text-slate-600 dark:text-slate-400">Funcionário fixo da empresa</p>
                        </div>
                        <div>
                          <Badge className="bg-indigo-100 text-indigo-800 mb-1">Terceiro</Badge>
                          <p className="text-slate-600 dark:text-slate-400">Prestador de serviço eventual</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <h3 className="font-medium mb-2 dark:text-slate-200">Ocorrências</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                        Registre eventos importantes sobre cada pessoa:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline">Advertência Verbal</Badge>
                        <Badge variant="outline">Advertência Escrita</Badge>
                        <Badge variant="outline">Suspensão</Badge>
                        <Badge variant="outline">Incidente Grave</Badge>
                        <Badge variant="outline" className="bg-green-50 border-green-200">Elogio</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* PROJETOS */}
              {activeSection === 'projetos' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold dark:text-slate-100">Projetos e Tarefas</h2>

                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <h3 className="font-medium mb-2 dark:text-slate-200 flex items-center gap-2">
                        <FolderKanban className="w-4 h-4" />
                        Projetos
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400">
                        <li>Organize por <strong>Departamento</strong> e <strong>Setor</strong></li>
                        <li>Visualize em <strong>Lista</strong> ou <strong>Kanban</strong></li>
                        <li>Acompanhe o <strong>status</strong> de cada projeto</li>
                        <li>Defina <strong>data de conclusão</strong> prevista</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <h3 className="font-medium mb-2 dark:text-slate-200 flex items-center gap-2">
                        <CheckSquare className="w-4 h-4" />
                        Tarefas
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400">
                        <li>Título e descrição detalhada</li>
                        <li>Responsáveis atribuídos</li>
                        <li>Data planejada e prazo</li>
                        <li>Checklist de subtarefas</li>
                        <li>Prioridade: <Badge className="bg-green-100 text-green-800 text-xs">Baixa</Badge> <Badge className="bg-yellow-100 text-yellow-800 text-xs">Média</Badge> <Badge className="bg-red-100 text-red-800 text-xs">Alta</Badge></li>
                        <li>Comentários da equipe</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                      <h3 className="font-medium text-violet-800 dark:text-violet-400 mb-2">Dica: Visualização no Dashboard</h3>
                      <p className="text-sm text-violet-700 dark:text-violet-300">
                        As tarefas com prazo definido aparecem automaticamente na <strong>Visão Semanal</strong> do Dashboard, 
                        junto com as OSs!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* CICLO DE MELHORIA */}
              {activeSection === 'ciclo' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold dark:text-slate-100">Ciclo de Melhoria</h2>

                  <div className="space-y-4">
                    <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
                      <h3 className="font-medium text-cyan-800 dark:text-cyan-400 mb-2 flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        O que é o Ciclo de Melhoria?
                      </h3>
                      <p className="text-sm text-cyan-700 dark:text-cyan-300">
                        O Ciclo de Melhoria é uma iniciativa mensal para acompanhar e melhorar os indicadores da empresa.
                        Cada mês tem um tema específico com metas a serem atingidas.
                      </p>
                    </div>

                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <h3 className="font-medium mb-2 dark:text-slate-200">Como funciona:</h3>
                      <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400">
                        <li>A cada mês, um novo ciclo é iniciado com tema e metas</li>
                        <li>As metas são acompanhadas através de indicadores</li>
                        <li>No Dashboard, você vê o progresso atual</li>
                        <li>Ao final do mês, os resultados são consolidados</li>
                      </ol>
                    </div>

                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <h3 className="font-medium text-amber-800 dark:text-amber-400 mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" />
                        Banco de Ideias
                      </h3>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                        Compartilhe suas sugestões de melhoria! Todas as ideias são avaliadas e as melhores podem ser implementadas.
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-amber-700 dark:text-amber-300">
                        <li>Envie ideias de qualquer área</li>
                        <li>Vote nas ideias de outros colegas</li>
                        <li>Acompanhe o status das sugestões</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* DICAS */}
              {activeSection === 'dicas' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold dark:text-slate-100">Dicas e Boas Práticas</h2>

                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-400">
                      <h3 className="font-medium text-green-800 dark:text-green-400 mb-2">
                        Boas práticas na Escala do Dia
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-green-700 dark:text-green-300">
                        <li>Preencha os apontamentos <strong>durante</strong> o dia, não deixe para depois</li>
                        <li>Sempre vincule os apontamentos à OS correta</li>
                        <li>Registre horários de entrada e saída com precisão</li>
                        <li>Informe o tipo de serviço realizado</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-400">
                      <h3 className="font-medium text-blue-800 dark:text-blue-400 mb-2">
                        Organização de Tarefas
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-blue-700 dark:text-blue-300">
                        <li>Sempre defina um <strong>prazo</strong> para suas tarefas</li>
                        <li>Use a <strong>prioridade</strong> para organizar o que é mais urgente</li>
                        <li>Atualize o status conforme avança</li>
                        <li>Use comentários para registrar progresso</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg border-l-4 border-violet-400">
                      <h3 className="font-medium text-violet-800 dark:text-violet-400 mb-2">
                        Navegação rápida
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-violet-700 dark:text-violet-300">
                        <li>Use o menu lateral para navegar entre as páginas</li>
                        <li>No Dashboard, clique em um dia para ver detalhes</li>
                        <li>Use o botão "Hoje" para voltar rapidamente à data atual</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-rose-50 dark:bg-rose-900/20 rounded-lg border-l-4 border-rose-400">
                      <h3 className="font-medium text-rose-800 dark:text-rose-400 mb-2 flex items-center gap-2">
                        <Heart className="w-4 h-4" />
                        Colabore com o sistema
                      </h3>
                      <p className="text-sm text-rose-700 dark:text-rose-300">
                        Encontrou algo que pode melhorar? Use o botão <strong>"Sugerir"</strong> no menu lateral 
                        para enviar suas ideias. Sua participação ajuda a tornar o dia a dia de todos mais organizado!
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
};

export default HelpPage;
